import {
  useState, memo, useEffect, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';

import LoaderIndicator from 'app/components/common/LoaderIndicator';

import { useSelector } from 'app/store/utils/redux/hooks';
import { ICashItem } from 'app/store/nodes/gift/interfaces';

import Search from './Search';
import Sort from './Sort';
import Grid from './Grid';
import Filter from './Filter';
import ModalConfirmation from './ModalConfirmation';

interface ModalChooseProps {
  hasHeader?: boolean,
  hasConfirm?: boolean,
  isOpened: boolean,
  isFixed?: boolean,
  amount: number,
  onClose?: () => void,
  onClosed?: () => void,
  onChoose?: (id: number) => void,
}

const ModalCashAvailableList = (props: ModalChooseProps) => {
  const {
    hasHeader = true,
    hasConfirm = true,
    isOpened,
    isFixed = false,
    amount,
    onClose,
    onClosed,
    onChoose,
  } = props;

  const classes = useStyles();

  const cash = useSelector((state) => state.gift.cash);

  const [chooseId, setChooseId] = useState<number>(0);
  const [isConfirmShow, setConfirmShow] = useState<boolean>(false);
  const [isMounted, setMounted] = useState(false);
  const [isShown, setShown] = useState(false);
  const [items, setItems] = useState<ICashItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<'popular' | 'alphabetical' | string>('popular');
  const [filter, setFilter] = useState<string[]>([]);

  const handleConfirmOpen = useCallback((id: number) => {
    if (hasConfirm) {
      setConfirmShow(true);
    } else {
      onChoose?.(chooseId);
    }
    setChooseId(id);
  }, [chooseId, hasConfirm, onChoose]);

  const handleConfirmClose = useCallback(() => {
    setConfirmShow(false);
  }, []);

  const handleConfirmChoose = useCallback(() => {
    setConfirmShow(false);
    setTimeout(() => { onChoose?.(chooseId); }, 300);
  }, [chooseId, onChoose]);

  const sortItems = [
    { alias: 'popular', label: 'Popular' },
    { alias: 'alphabetical', label: 'Alphabetical' },
  ];

  useEffect(() => {
    if (isFixed) {
      setMounted(true);
      return;
    }
    if (isOpened && !isShown) {
      setMounted(true);
      setTimeout(() => {
        setShown(true);
      }, 10);
    }
    if (!isOpened && isShown) {
      setShown(false);
      setTimeout(() => {
        setMounted(false);
        if (onClosed) {
          onClosed();
        }
      }, 300);
    }
  }, [isFixed, isMounted, isOpened, isShown, onClosed]);

  useEffect(() => {
    const found = cash.list.filter((item) => {
      const isPhraseContains = item.name.toLowerCase().includes(searchText.toLowerCase());

      if (!filter.length) {
        return isPhraseContains;
      }
      let isInCategory = false;
      filter.forEach((category) => {
        if (item.categories.includes(category)) {
          isInCategory = true;
        }
      });

      return isPhraseContains && isInCategory;
    });
    found.sort((a, b) => {
      const isByPopular = sortBy === 'popular';
      const isByAlphabetical = sortBy === 'alphabetical';
      if (isByPopular && a.rank > b.rank) {
        return 1;
      }
      if (isByPopular && a.rank < b.rank) {
        return -1;
      }
      if (isByPopular && a.name > b.name) {
        return 1;
      }
      if (isByPopular && a.name < b.name) {
        return -1;
      }
      if (isByAlphabetical && a.name > b.name) {
        return 1;
      }
      if (isByAlphabetical && a.name < b.name) {
        return -1;
      }
      return 0;
    });
    setItems(found);
  }, [cash.list, searchText, sortBy, filter]);

  const { isLoading } = cash.meta;

  const selectedGiftName = chooseId > 0 ? (items || [])?.find((item) => item.id === chooseId)?.name : '';

  return createPortal(
    isMounted && (
      <>
        <div
          className={classnames(classes.container, { [classes.isShown]: isShown, [classes.isFixed]: isFixed })}
        >
          <div className={classes.panel}>
            {hasHeader && (
              <div className={classes.header}>
                <button type="button" onClick={onClose}>
                  <svg viewBox="0 0 24 20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 8.75C22.6904 8.75 23.25 9.30964 23.25 10C23.25 10.6904 22.6904 11.25 22 11.25V8.75ZM1.11612 10.8839C0.627962 10.3957 0.627962 9.60427 1.11612 9.11612L9.07107 1.16117C9.55922 0.67301 10.3507 0.67301 10.8388 1.16117C11.327 1.64932 11.327 2.44078 10.8388 2.92893L3.76777 10L10.8388 17.0711C11.327 17.5592 11.327 18.3507 10.8388 18.8388C10.3507 19.327 9.55922 19.327 9.07107 18.8388L1.11612 10.8839ZM22 11.25H2V8.75H22V11.25Z" fill="#4A4A4A" />
                  </svg>
                  <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g transform="scale(1.7 1.7) translate(-6 -6)">
                      <path d="M22.0703 22.0712L7.92818 7.92903" stroke="#4a4a4a" strokeWidth="2" strokeLinecap="round" />
                      <path d="M7.92969 22.0712L22.0718 7.92903" stroke="#4a4a4a" strokeWidth="2" strokeLinecap="round" />
                    </g>
                  </svg>
                </button>
                <div>{`Choose $${amount} gift card`}</div>
              </div>
            )}
            <div className={classes.content}>
              <Search disabled={isLoading} className={classes.search} onChange={setSearchText} value={searchText} />
              <div className={classes.toolbar}>
                <Sort disabled={isLoading} items={sortItems} value={sortBy} onChange={setSortBy} />
                <Filter disabled={isLoading} items={cash.categories} values={filter} onChange={setFilter} />
              </div>
              {isLoading && (
                <div className={classes.loader}>
                  <LoaderIndicator />
                </div>
              )}
              {!isLoading && <Grid className={classes.items} items={items} onChoose={handleConfirmOpen} />}
            </div>
            <ModalConfirmation
              isOpened={isConfirmShow}
              message={(
                <>
                  {`You will receive ${/^[aeiou]/i.test(selectedGiftName || '') ? 'an' : 'a'} ${selectedGiftName} gift card.`}
                </>
              )}
              onClose={handleConfirmClose}
              onConfirm={handleConfirmChoose}
            />
          </div>
        </div>
      </>
    ),
    document.body,
  );
};

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 10,
    opacity: 0,
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(124, 124, 124, 0.25)',
    transition: '300ms opacity ease',
  },
  panel: {
    '&': {
      position: 'relative',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      background: '#ffffff', // #fcf7f4
      padding: '1.8rem 2.4rem 0',
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: '102.4rem',
      height: '100%',
      boxShadow: '0 0 3rem rgba(0, 0, 0, 0.10)',
      overflow: 'hidden',
    },
    '@media (min-width: 1025px)': {
      flex: 'unset',
      width: '90%',
      height: '90%',
      borderRadius: '1.4rem',
      paddingBottom: '2.2rem',
    },
  },
  isFixed: {
    transition: 'none',
    opacity: 1,
  },
  isShown: {
    opacity: 1,
  },
  header: {
    '&': {
      position: 'relative',
    },
    '& button': {
      '&': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '4.3rem',
        height: '4.3rem',
        padding: '1rem',
        margin: '-1rem',
        background: 'none',
        border: 0,
        cursor: 'pointer',
      },
      '& svg': {
        '&': {
          width: '100%',
          height: '100%',
        },
        '&:first-child': {
          '@media (min-width: 1025px)': {
            display: 'none',
          },
        },
        '&:last-child': {
          '@media (max-width: 1024px)': {
            display: 'none',
          },
        },
      },
      '@media (min-width: 1025px)': {
        left: 'unset',
        right: 0,
      },
    },
    '& div': {
      color: '#4A4A4A', // #39C4BD
      fontSize: '2rem',
      fontWeight: 700,
      textAlign: 'center',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  toolbar: {
    display: 'flex',
    marginTop: '1.4rem',
    justifyContent: 'space-between',
  },
  search: {
    marginTop: '1.4rem',
  },
  items: {
    marginTop: '1.6rem',
  },
  loader: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '11rem',
  },
});

export default memo(ModalCashAvailableList);
